import { Badge, Dialog, Box, Flex, Text, Heading, ScrollArea, IconButton } from '@radix-ui/themes';
import React, { useState } from 'react';
import { MdOutlineVerifiedUser, MdMedication, MdAlarm } from 'react-icons/md';
import { CgArrowsExpandUpRight } from 'react-icons/cg';
import { Cross2Icon } from '@radix-ui/react-icons'; 
import { formatApiDate, formatApiTime } from '../../utils/helpers';

const ViewAlarmHistory = ({ initialValues, onHistoryUpdated, roleId }) => {
  const [isViewOpen, setViewOpen] = useState(false);

  // Check if the role requires the special, simplified UI
  const isSpecialRole = [5, 6, 7].includes(roleId);

  // Master Alarm ID mapping
  const masterAlarmMapping = {
    1: 'Diaper',
    2: 'Wake Up',
    3: 'Nurse Call',
    4: 'Patient Rolling',
    5: 'F&B',
    6: 'Security',
    7: 'Housekeeping',
    8: 'Patient Out Of Room',
    9: 'Medication'
  };

  // Format date and time
  const formatDateTime = (dateString) => {
    return {
      date: formatApiDate(dateString),
      time: formatApiTime(dateString)
    };
  };

  const getStatusBadge = (status) => {
    // If the role is special, the parent component already re-maps the status to 'Resolved'.
    // This function will correctly apply the 'green' color for 'Resolved'.
    const statusColors = {
      'Resolved': 'green',
      'Timeout': 'tomato',
      'Pending': 'yellow',
      'Confirmed': 'blue',
      'Responded': 'orange'
    };
    const color = statusColors[status] || 'gray';
    return (
      <Badge color={color} size="2">
        <MdOutlineVerifiedUser size={16} />
        {status}
      </Badge>
    );
  };

  const getAlarmIcon = (masterAlarmId) => {
    if (masterAlarmId === 9) {
      return <MdMedication size={24} color="#0091ff" />;
    }
    return <MdAlarm size={24} color="#0091ff" />;
  };

  return (
    <Dialog.Root open={isViewOpen} onOpenChange={setViewOpen}>
      <Dialog.Trigger>
        <IconButton variant="solid">
          <CgArrowsExpandUpRight width="18" height="18" />
        </IconButton>
      </Dialog.Trigger>
      
      <Dialog.Content maxWidth="600px">
        <Dialog.Close>
          <IconButton
            size="1"
            variant="ghost"
            color="gray"
            style={{ position: 'absolute', top: '16px', right: '16px', cursor: 'pointer' }}
          >
            <Cross2Icon width="18" height="18" />
          </IconButton>
        </Dialog.Close>
        
        <Dialog.Title>
          <Flex align="center" gap="2">
            {getAlarmIcon(initialValues.masterAlarmId)}
            Alarm History Details
          </Flex>
        </Dialog.Title>

        {/* Status and Basic Info */}
        <Box my="4" p="3" style={{ backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
            <Flex direction="column" gap="3">
              <Flex justify="between" align="center">
                <Heading size="4">{masterAlarmMapping[initialValues.masterAlarmId] || 'Unknown Alarm'}</Heading>
                {/* The badge will correctly show 'Resolved' in green for special roles */}
                {getStatusBadge(initialValues.status)}
              </Flex>
              
              <Flex gap="4" wrap="wrap">
                <Flex direction="column" gap="1" style={{ minWidth: '150px' }}>
                  <Text size="1" color="gray">PATIENT REFERENCE</Text>
                  <Text size="3" weight="bold">{initialValues.patientReferenceNumber || 'N/A'}</Text>
                </Flex>
                
                <Flex direction="column" gap="1" style={{ minWidth: '120px' }}>
                  <Text size="1" color="gray">ROOM NUMBER</Text>
                  <Text size="3" weight="bold">{initialValues.roomNumber || 'N/A'}</Text>
                </Flex>
                
                <Flex direction="column" gap="1" style={{ minWidth: '120px' }}>
                  <Text size="1" color="gray">BED NUMBER</Text>
                  <Text size="3" weight="bold">{initialValues.bedNumber || 'N/A'}</Text>
                </Flex>
              </Flex>
              
              <Flex gap="4" wrap="wrap">
                <Flex direction="column" gap="1" style={{ flex: 1 }}>
                  <Text size="1" color="gray">LOCATION</Text>
                  <Text size="2">{initialValues.department || 'N/A'}</Text>
                </Flex>
                
                <Flex direction="column" gap="1" style={{ minWidth: '150px' }}>
                  <Text size="1" color="gray">ATTENDEE</Text>
                  <Text size="2">{initialValues.attendee || 'N/A'}</Text>
                </Flex>
              </Flex>
            </Flex>
        </Box>

        {/* Timeline Section - Conditionally rendered based on role */}
        <Box mb="4">
          <Heading size="3" mb="3">Timeline</Heading>
          
          {isSpecialRole ? (
            // --- SIMPLIFIED TIMELINE for roles 5, 6, 7 ---
            <Flex direction="column" gap="3">
              <Flex gap="3" align="center">
                <Box style={{ width: '12px', height: '12px', backgroundColor: '#10b981', borderRadius: '50%' }} />
                <Flex direction="column" gap="1" style={{ flex: 1 }}>
                  <Text size="2" weight="bold">Resolved</Text>
                  {initialValues.confirmedOn ? (
                    <Flex gap="2">
                      <Text size="1" color="gray">{formatDateTime(initialValues.confirmedOn).date}</Text>
                      <Text size="1" color="gray">{formatDateTime(initialValues.confirmedOn).time}</Text>
                    </Flex>
                  ) : (
                    <Text size="1" color="gray">Not resolved</Text>
                  )}
                </Flex>
              </Flex>
            </Flex>
          ) : (
            // --- FULL TIMELINE for all other roles ---
            <Flex direction="column" gap="3">
              {/* Notified On */}
              <Flex gap="3" align="center">
                <Box style={{ width: '12px', height: '12px', backgroundColor: '#0091ff', borderRadius: '50%' }} />
                <Flex direction="column" gap="1" style={{ flex: 1 }}>
                  <Text size="2" weight="bold">Notified</Text>
                  {initialValues.notifiedOn ? (
                    <Flex gap="2">
                      <Text size="1" color="gray">{formatDateTime(initialValues.notifiedOn).date}</Text>
                      <Text size="1" color="gray">{formatDateTime(initialValues.notifiedOn).time}</Text>
                    </Flex>
                  ) : <Text size="1" color="gray">Not available</Text>}
                </Flex>
              </Flex>

              {/* Confirmed On */}
              <Flex gap="3" align="center">
                <Box style={{ width: '12px', height: '12px', backgroundColor: initialValues.confirmedOn ? '#22c55e' : '#e5e7eb', borderRadius: '50%' }} />
                <Flex direction="column" gap="1" style={{ flex: 1 }}>
                  <Text size="2" weight="bold" color={initialValues.confirmedOn ? undefined : 'gray'}>Confirmed</Text>
                  {initialValues.confirmedOn ? (
                    <Flex gap="2">
                      <Text size="1" color="gray">{formatDateTime(initialValues.confirmedOn).date}</Text>
                      <Text size="1" color="gray">{formatDateTime(initialValues.confirmedOn).time}</Text>
                    </Flex>
                  ) : <Text size="1" color="gray">Not confirmed</Text>}
                </Flex>
              </Flex>

              {/* Responded On */}
              <Flex gap="3" align="center">
                <Box style={{ width: '12px', height: '12px', backgroundColor: initialValues.respondedOn ? '#f59e0b' : '#e5e7eb', borderRadius: '50%' }} />
                <Flex direction="column" gap="1" style={{ flex: 1 }}>
                  <Text size="2" weight="bold" color={initialValues.respondedOn ? undefined : 'gray'}>Responded</Text>
                  {initialValues.respondedOn ? (
                    <Flex gap="2">
                      <Text size="1" color="gray">{formatDateTime(initialValues.respondedOn).date}</Text>
                      <Text size="1" color="gray">{formatDateTime(initialValues.respondedOn).time}</Text>
                    </Flex>
                  ) : <Text size="1" color="gray">No response</Text>}
                </Flex>
              </Flex>

              {/* Resolved On */}
              <Flex gap="3" align="center">
                <Box style={{ width: '12px', height: '12px', backgroundColor: initialValues.resolvedOn ? '#10b981' : '#e5e7eb', borderRadius: '50%' }} />
                <Flex direction="column" gap="1" style={{ flex: 1 }}>
                  <Text size="2" weight="bold" color={initialValues.resolvedOn ? undefined : 'gray'}>Resolved</Text>
                  {initialValues.resolvedOn ? (
                    <Flex gap="2">
                      <Text size="1" color="gray">{formatDateTime(initialValues.resolvedOn).date}</Text>
                      <Text size="1" color="gray">{formatDateTime(initialValues.resolvedOn).time}</Text>
                    </Flex>
                  ) : <Text size="1" color="gray">Not resolved</Text>}
                </Flex>
              </Flex>
            </Flex>
          )}
        </Box>

        {/* Comments Section */}
        {initialValues.comment && (
            <Box mb="4">
              <Heading size="3" mb="2">Comments</Heading>
              <Box p="3" style={{ backgroundColor: '#fef3c7', borderRadius: '6px', borderLeft: '4px solid #f59e0b' }}>
                <Text size="2">{initialValues.comment}</Text>
              </Box>
            </Box>
        )}

        {/* Medication/Action Details */}
        {initialValues.masterAlarmId === 9 && initialValues.medicActionDetails && initialValues.medicActionDetails.length > 0 && (
          <Box>
            <Heading size="3" mb="2">Medication/Action Details</Heading>
            <ScrollArea style={{ maxHeight: '200px' }}>
                {initialValues.medicActionDetails.map((detail, index) => (
                  <Box 
                    key={index} 
                    p="3" 
                    mb="2" 
                    style={{ 
                      backgroundColor: '#f5f8ff', 
                      borderRadius: '6px',
                      borderLeft: '4px solid #0091ff'
                    }}
                  >
                    <Flex direction="column" gap="1">
                      <Text weight="bold" size="3">{detail.medication}</Text>
                      
                      <Flex gap="3" mt="1">
                        {detail.dosage && (
                          <Flex direction="column" gap="1">
                            <Text size="1" color="gray">DOSAGE</Text>
                            <Text size="2">{detail.dosage}</Text>
                          </Flex>
                        )}
                        
                        {detail.root && (
                          <Flex direction="column" gap="1">
                            <Text size="1" color="gray">ROOT</Text>
                            <Text size="2">{detail.root}</Text>
                          </Flex>
                        )}
                      </Flex>
                    </Flex>
                  </Box>
                ))}
            </ScrollArea>
          </Box>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default ViewAlarmHistory;
